import React from "react";

import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";

const TABLE_MAX_HEIGHT = 300;

const useStyles = makeStyles((theme) => ({
  container: {
    maxHeight: TABLE_MAX_HEIGHT,
    width: "100%",
  },
  table: {
    tableLayout: "fixed",
    width: "100%",
  },
  headCell: {
    fontWeight: theme.typography.fontWeightBold,
    backgroundColor: theme.palette.background.paper,
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
  },
  emptyCell: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottom: "none",
  },
}));

/** Shared dense cell + input styles for Profile Settings tables only. */
export const useProfileSettingsTableInputStyles = makeStyles((theme) => ({
  tableCellDense: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    verticalAlign: "middle",
  },
  tableTextField: {
    margin: 0,
    "& .MuiOutlinedInput-root": {
      fontSize: theme.typography.body2.fontSize,
    },
    "& .MuiOutlinedInput-input": {
      padding: "5px 8px",
    },
  },
  tableFormControl: {
    margin: 0,
    "& .MuiOutlinedInput-root": {
      fontSize: theme.typography.body2.fontSize,
    },
    "& .MuiSelect-select": {
      padding: "5px 32px 5px 8px",
      minHeight: "unset",
    },
    "& .MuiOutlinedInput-input": {
      padding: "5px 8px",
    },
  },
}));

export interface ProfileSettingsTableColumn {
  id: string;
  label: string;
  width?: string | number;
  align?: "left" | "right" | "center";
}

interface ProfileSettingsTableProps {
  columns: ProfileSettingsTableColumn[];
  children: React.ReactNode;
  emptyMessage?: string;
}

function ProfileSettingsTable({
  columns,
  children,
  emptyMessage = "Empty",
}: ProfileSettingsTableProps) {
  const classes = useStyles();
  const isEmpty = React.Children.toArray(children).length === 0;

  return (
    <TableContainer className={classes.container}>
      <Table stickyHeader size="small" className={classes.table}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.id}
                align={column.align}
                className={classes.headCell}
                style={
                  column.width !== undefined
                    ? { width: column.width }
                    : undefined
                }
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isEmpty ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                align="center"
                className={classes.emptyCell}
              >
                <Typography variant="body2" color="textSecondary">
                  {emptyMessage}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            children
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function sortRowsByKey<T>(
  rows: T[],
  getSortKey: (row: T) => string
): T[] {
  return [...rows].sort((a, b) =>
    getSortKey(a).localeCompare(getSortKey(b), undefined, {
      sensitivity: "base",
    })
  );
}

export default ProfileSettingsTable;
